/**
 * ═══ LA PIÈCE JOINTE D'UN CHAMP, TELLE QU'ELLE EST RANGÉE ═══
 *
 * Un champ « Pièce jointe » enregistre une CHAÎNE dans `extended`, comme un texte
 * libre — donc rien à changer côté serveur, dans l'export ni dans la liste des
 * participants (voir [extended-values-must-be-strings]). Sa forme :
 *
 *     file:<clé du seau privé>:<nom d'origine>
 *
 * La clé est un UUID v4 ; le nom vient après le second deux-points et peut lui-même
 * en contenir. Le fichier vit dans le seau PRIVÉ : ce qu'on range n'est PAS une
 * adresse, on en demande une, signée et courte, au moment d'ouvrir (`api/upload.ts`).
 *
 * Pur, donc testé à part : c'est ici qu'une valeur mal formée doit être reconnue
 * comme « rien », plutôt que rendue comme un lien cassé.
 */

export type Attachment = { uuid: string; name: string };

const PREFIX = "file:";
const UUID = /^[0-9a-f]{8}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{12}$/i;

export function parseAttachment(value: unknown): Attachment | null {
  if (typeof value !== "string" || !value.startsWith(PREFIX)) return null;
  const rest = value.slice(PREFIX.length);
  const colon = rest.indexOf(":");
  const uuid = colon === -1 ? rest : rest.slice(0, colon);
  const name = colon === -1 ? "" : rest.slice(colon + 1).trim();
  if (!UUID.test(uuid)) return null;
  return { uuid, name: name || uuid };
}

export function formatAttachment(a: Attachment): string {
  // Un nom sans retour à la ligne, et tronqué : c'est un libellé, pas un contenu.
  const name = a.name.replace(/[\r\n]+/g, " ").trim().slice(0, 200);
  return `${PREFIX}${a.uuid}:${name}`;
}

/**
 * CE QUE LE BACK OFFICE ACCEPTE — la même syntaxe que l'attribut `accept` d'un
 * `<input type="file">` (`.pdf`, `image/*`, `application/pdf`), séparés par des
 * virgules. Vide = tout.
 *
 * ⚠️ REVÉRIFIÉ ICI, pas seulement par l'attribut : sur certains navigateurs
 * mobiles `accept` n'est qu'une suggestion, et un glisser-déposer l'ignore.
 */
export function accepts(accept: string | undefined, file: { name: string; type: string }): boolean {
  const rules = (accept ?? "")
    .split(",")
    .map((r) => r.trim().toLowerCase())
    .filter(Boolean);
  if (rules.length === 0) return true;
  const name = file.name.toLowerCase();
  const type = file.type.toLowerCase();
  return rules.some((rule) => {
    if (rule.startsWith(".")) return name.endsWith(rule);
    if (rule.endsWith("/*")) return type.startsWith(rule.slice(0, -1));
    return type === rule;
  });
}

/** Le plafond par défaut, quand le back office n'en règle pas : 10 Mo. */
export const DEFAULT_MAX_MB = 10;
